import { createSelector } from 'reselect';

import { ModalService } from 'ui-components/services/modal-service';

import { Providers } from '../enums/providers';
import Global from '../store/global-selectors';
import { SchedulePickupPrompt } from '../carrier-pickup/carrier-pickup-modal-v2/schedule-pickup-prompt';
import { UserSettings } from '../enums/user-settings/user-settings';

const selectIsUserAwareOfAmazonPickupRequirements = createSelector(Global.selectUserUserSettings, (userSettings) => {
  return !!userSettings.find(
    (setting) => setting.settingName === UserSettings.isAmazonShippingFirstLabelCreated.settingName && setting.settingValue === 'true',
  );
});

// This file is written like it's generic but really, it's very specific to Amazon Shipping
// And there's no UX input on what happens if there are multiple shipments with different carriers required to schedule pickups
/**
 * @typedef {import('../prop-types/fulfillment-plan').FulfillmentPlanPropType} FulfillmentPlanPropType
 */
/**
 * @param {object} state - the redux state
 * @param {FulfillmentPlanPropType[]} [fulfillmentPlans] - the fulfillment plans
 * @returns {FulfillmentPlanPropType[]} - the fulfillment plans that are amazon shipments needing pickup scheduled
 */
export const getAmazonShipments = (state, fulfillmentPlans) => {
  const sellerLabelProviders = Global.selectSellerLabelProviders(state);
  const isUserAwareOfPickupRequirements = selectIsUserAwareOfAmazonPickupRequirements(state);

  if (isUserAwareOfPickupRequirements) {
    return [];
  }

  return fulfillmentPlans.filter((plan) => {
    const { labelConfiguration } = plan;
    const amazonSellerLabelProviderId = sellerLabelProviders.find(
      (provider) => provider.labelProviderId === Providers.AmazonShippingWalleted.labelProviderId,
    )?.sellerLabelProviderId;
    // Check if this fulfillment plan is using Amazon Shipping and has a service collection GUID (indicating its picked by rate shopper)
    return labelConfiguration?.sellerLabelProviderId === amazonSellerLabelProviderId && !!labelConfiguration?.serviceCollectionGuid;
  });
};

const isThereAnAmazonShipment = (fulfillmentPlans, amazonSellerLabelProviderId) => {
  const hasAmazonShipment = fulfillmentPlans.some((plan) => {
    const { labelConfiguration } = plan;
    return labelConfiguration?.sellerLabelProviderId === amazonSellerLabelProviderId;
  });
  return hasAmazonShipment;
};

/**
 * @template T
 * @param {object} state - the redux state
 * @param {string[]} [fulfillmentPlanIds] - the fulfillment plan ids to check
 * @returns {string}
 */
export const findSellerProviderIdNeedingPickupPrompt = (state, fulfillmentPlanIds = []) => {
  const isUserAwareOfPickupRequirements = selectIsUserAwareOfAmazonPickupRequirements(state);

  if (isUserAwareOfPickupRequirements) {
    return null;
  }

  const sellerLabelProviders = Global.selectSellerLabelProviders(state);
  const amazonSellerLabelProviderId = sellerLabelProviders.find(
    (provider) => provider.labelProviderId === Providers.AmazonShippingWalleted.labelProviderId,
  )?.sellerLabelProviderId;

  if (!amazonSellerLabelProviderId) {
    return null;
  }

  const fulfillmentPlans = Global.selectEntitiesFulfillmentPlansByIds(state, { fulfillmentPlanIds });
  const hasAmazonShipment = isThereAnAmazonShipment(fulfillmentPlans, amazonSellerLabelProviderId);
  return hasAmazonShipment ? amazonSellerLabelProviderId : null;
};

export const openPickupPromptIfAppropriate = (sellerProviderIdForPickupPrompt) => {
  if (!sellerProviderIdForPickupPrompt) {
    return;
  }

  const modalId = ModalService.launchModal({
    component: SchedulePickupPrompt,
    props: {
      sellerLabelProviderId: sellerProviderIdForPickupPrompt,
      onClose: () => {
        ModalService.closeModalById(modalId);
      },
    },
  });
};
